import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { useAppContext } from "../context/AppContext";
import { HomeIcon, BagIcon, CardIcon, HeartIcon, BellIcon } from "./Icons";
import Toast from "./Toast";
import DishImage from "./DishImage";
import CallWaiterSheet from "./CallWaiterSheet";
import { FAVORITES_TAB, ALL_TAB } from "../util/menuTabs";
import { mockCallWaiter } from "../mocks";
import useKeyboardOpen from "../hooks/useKeyboardOpen";

const FLY_SIZE = 64;

/**
 * The dish just added, flying from where its "add to order" button was into the order bag:
 * up in an arc from the bottom of the screen, shrinking as it lands.
 */
const FlyingDish = ({ dish, from, to, onLanded }) => {
  const half = FLY_SIZE / 2;
  const x = [from.x, from.x, (from.x + to.x) / 2, to.x].map((v) => v - half);
  const y = [from.y, from.y - 10, Math.min(from.y, to.y) - 140, to.y].map((v) => v - half);
  return (
    <motion.div
      aria-hidden="true"
      className="fixed top-0 left-0 z-[60] pointer-events-none"
      initial={{ x: x[0], y: y[0], scale: 0.5, opacity: 0 }}
      animate={{ x, y, scale: [0.5, 1, 0.85, 0.3], opacity: [0, 1, 1, 0.8] }}
      transition={{ duration: 0.9, delay: 0.15, ease: "easeInOut", times: [0, 0.2, 0.55, 1] }}
      onAnimationComplete={onLanded}
    >
      <DishImage src={dish.image_url} seed={dish.seed} size={FLY_SIZE} />
    </motion.div>
  );
};

/**
 * The red bar at the bottom of the menu: menu, order, payment, favorites, and the round
 * call-a-waiter button in the middle.
 */
const BottomNavBar = ({ navigate, setBottomHeight }) => {
  const location = useLocation();
  const bottomRef = useRef(null);
  const bagRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { t } = useTranslation();
  const { currentOrderList, isAuthenticated, selectedTab, setSelectedTab, tableLabel } =
    useAppContext();
  const keyboardOpen = useKeyboardOpen();
  const [toast, setToast] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [calling, setCalling] = useState(false);
  const [flying, setFlying] = useState(null);
  const [bagBumps, setBagBumps] = useState(0);
  const count = currentOrderList?.items?.length || 0;
  const onMenu = location.pathname === "/";
  const onFavorites = onMenu && selectedTab === FAVORITES_TAB;
  const addedDish = location.state?.addedDish;

  // Back from a dish's screen with the dish just added: show it landing in the order bag.
  useEffect(() => {
    if (!addedDish) return;
    // Clear the state, so going back or reloading does not play it again.
    navigate(location.pathname, { replace: true, state: null });
    const landed = () => {
      setFlying(null);
      setBagBumps((n) => n + 1);
      setToast(t("addedToOrder", { name: addedDish.name }));
    };
    const bag = bagRef.current?.getBoundingClientRect();
    if (reduceMotion || !bag) {
      landed();
      return;
    }
    setFlying({
      dish: addedDish,
      from: { x: window.innerWidth / 2, y: window.innerHeight - 160 },
      to: { x: bag.left + bag.width / 2, y: bag.top + bag.height / 2 },
      landed,
    });
  }, [addedDish, location.pathname, navigate, reduceMotion, t]);

  useEffect(() => {
    const el = bottomRef.current;
    if (!el || !setBottomHeight) return;
    const update = () => setBottomHeight(el.offsetHeight);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [setBottomHeight]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(""), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  const callWaiter = async () => {
    setCalling(true);
    await mockCallWaiter();
    setCalling(false);
    setConfirmOpen(false);
    setToast(t("waiterOnTheWay", { table: tableLabel }));
  };
  const goToPayment = () => navigate(isAuthenticated ? "/payment" : "/current-order");

  const item = "flex-1 h-19 flex flex-col items-center justify-center gap-1 text-dark";

  return (
    <>
      <Toast message={toast} />
      {flying && (
        <FlyingDish dish={flying.dish} from={flying.from} to={flying.to} onLanded={flying.landed} />
      )}
      <CallWaiterSheet
        open={confirmOpen}
        calling={calling}
        onConfirm={callWaiter}
        onClose={() => setConfirmOpen(false)}
        onOrder={() => {
          setConfirmOpen(false);
          if (onFavorites) setSelectedTab(ALL_TAB);
          navigate("/");
        }}
        onPay={() => {
          setConfirmOpen(false);
          goToPayment();
        }}
      />
      <nav
        ref={bottomRef}
        aria-label={t("Main navigation")}
        className={`fixed bottom-0 inset-x-0 z-40 bg-background shadow-sheet rounded-t-xl flex items-center ${keyboardOpen ? "invisible" : ""}`}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <button
          type="button"
          className={item}
          aria-label={t("Home")}
          aria-current={onMenu && !onFavorites ? "page" : undefined}
          onClick={() => {
            if (onFavorites) setSelectedTab(ALL_TAB);
            navigate("/");
          }}
        >
          <HomeIcon />
          <span className={`w-[5px] h-[5px] rounded-full ${onMenu && !onFavorites ? "bg-primary" : "bg-transparent"}`} />
        </button>
        <button
          ref={bagRef}
          type="button"
          className={`${item} relative`}
          aria-label={t("View cart")}
          onClick={() => navigate("/current-order")}
        >
          {/* Re-keyed on every landing so the bump plays again. */}
          <motion.span
            key={`bag-${bagBumps}`}
            className="flex"
            animate={bagBumps ? { scale: [1, 1.35, 0.9, 1], rotate: [0, -12, 8, 0] } : undefined}
            transition={{ duration: 0.45, ease: "easeOut" }}
          >
            <BagIcon />
          </motion.span>
          {count > 0 && (
            <motion.span
              key={`badge-${bagBumps}`}
              initial={bagBumps ? { scale: 0.3 } : false}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 14 }}
              className="absolute top-[18px] start-1/2 ms-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[12px] font-bold flex items-center justify-center tabular-nums"
            >
              {count}
            </motion.span>
          )}
          <span className="w-[5px] h-[5px]" />
        </button>
        <div className="flex-[1.2]" aria-hidden="true" />
        <button
          type="button"
          className={item}
          aria-label={t("Payment")}
          onClick={goToPayment}
        >
          <CardIcon />
          <span className="w-[5px] h-[5px]" />
        </button>
        <button
          type="button"
          className={item}
          aria-label={t("Favorites")}
          aria-current={onFavorites ? "page" : undefined}
          onClick={() => {
            setSelectedTab(FAVORITES_TAB);
            navigate("/");
          }}
        >
          <HeartIcon filled={onFavorites} />
          <span className={`w-[5px] h-[5px] rounded-full ${onFavorites ? "bg-primary" : "bg-transparent"}`} />
        </button>
      </nav>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        aria-label={t("Call a waiter")}
        className={`fixed z-50 left-1/2 -translate-x-1/2 w-19 h-19 rounded-full border-[6px] border-white bg-primary text-white shadow-brand flex items-center justify-center ${keyboardOpen ? "invisible" : ""}`}
        style={{ bottom: "calc(40px + env(safe-area-inset-bottom, 0px))" }}
      >
        <BellIcon size={28} />
      </button>
    </>
  );
};

export default BottomNavBar;
